import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, MapPin, ArrowSquareOut } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterclassDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [masterclass, setMasterclass] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMasterclass();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMasterclass = async () => {
    try {
      const response = await axios.get(`${API}/masterclasses/${id}`);
      setMasterclass(response.data);
    } catch {
      toast.error('Masterclass not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = async () => {
    try {
      await axios.post(`${API}/clicks?masterclass_id=${masterclass.id}`);
      window.open(masterclass.external_link, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(masterclass.external_link, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-900 text-xl">Loading...</div>
      </div>
    );
  }

  if (!masterclass) return null;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/marketplace')}
            className="text-white/90 hover:text-white hover:bg-transparent"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="mr-2" /> Back to Marketplace
          </Button>
        </div>
      </nav>

      <div className="pt-24 px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-xs uppercase tracking-wider text-[#00a3af] font-bold mb-4" data-testid="category">{masterclass.category}</div>
                <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-6" data-testid="title">{masterclass.title}</h1>
                
                <div className="flex items-center gap-4 mb-8 pb-8 border-b border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#CCFF00] to-[#00F0FF] flex items-center justify-center font-bold text-black">
                    {masterclass.instructor_name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold" data-testid="instructor-name">{masterclass.instructor_name}</div>
                    <div className="text-sm text-gray-600">Instructor</div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-4">About This Masterclass</h2>
                  <p className="text-gray-600 leading-relaxed" data-testid="description">{masterclass.description}</p>
                </div>

                {masterclass.tags && masterclass.tags.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4">Topics Covered</h3>
                    <div className="flex flex-wrap gap-2">
                      {masterclass.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm"
                          data-testid={`tag-${i}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gray-50 border border-gray-200 rounded-lg p-8 sticky top-28"
              >
                <div className="mb-6">
                  <div className="text-4xl font-black gradient-text mb-2" data-testid="price">{masterclass.price}</div>
                  {masterclass.price.toLowerCase() !== 'free' && !masterclass.price.toLowerCase().includes('free') && (
                    <div className="text-sm text-gray-600">One-time payment</div>
                  )}
                </div>

                <Button
                  onClick={handleRegisterClick}
                  className="w-full bg-[#34c759] text-black hover:bg-[#28a745] rounded-full py-6 text-lg font-bold neon-glow mb-6"
                  data-testid="register-btn"
                >
                  Register Now <ArrowSquareOut size={20} className="ml-2" weight="bold" />
                </Button>

                <div className="space-y-4 border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-3">
                    <Calendar size={24} className="text-[#00a3af]" weight="duotone" />
                    <div>
                      <div className="text-sm text-gray-600">Date</div>
                      <div className="font-bold" data-testid="date">{masterclass.date}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock size={24} className="text-[#00a3af]" weight="duotone" />
                    <div>
                      <div className="text-sm text-gray-600">Duration</div>
                      <div className="font-bold" data-testid="duration">{masterclass.duration}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin size={24} className="text-[#00a3af]" weight="duotone" />
                    <div>
                      <div className="text-sm text-gray-600">Format</div>
                      <div className="font-bold capitalize" data-testid="format">{masterclass.format}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    ⚡ You'll be redirected to the instructor's registration page
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}